import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import {
    ArrowRight,
    Drumstick,
    Moon,
    Timer
} from 'lucide-react'
import { buttonClasses } from '@/components/generals/button'
import { PorridgeIllustration } from '@/components/landing/porridge-illustration'
import { formatTime, getFirstOpeningTime } from '@/lib/hours'

const highlights = [
    { icon: Moon, title: "Overnight broth", text: "Six hours on the stove" },
    { icon: Drumstick, title: "Free-range chicken", text: "Pulled by hand daily" },
    { icon: Timer, title: "Ready in minutes", text: "Order ahead, skip the line" }
]

const firstBowls = formatTime(getFirstOpeningTime())

export const HeroSection: FC = (): ReactNode => {
    return (
        <section id='hero' aria-labelledby='hero-title' className='relative isolate overflow-hidden'>
            <div aria-hidden='true' className='pointer-events-none absolute inset-0 -z-10'>
                <div className='absolute -left-40 -top-40 size-[34rem] rounded-full bg-yolk-soft blur-3xl' />
                <div className='absolute -right-32 top-1/3 size-[28rem] rounded-full bg-primary/10 blur-3xl' />
            </div>

            <div className='mx-auto grid max-w-6xl items-center gap-14 px-5 pb-20 pt-8 sm:px-8 lg:grid-cols-[1.05fr_1fr] lg:gap-16 lg:pb-28 lg:pt-10'>
                <div className='animate-in fade-in slide-in-from-bottom-4 duration-700 ease-out'>
                    <p className='inline-flex items-center gap-2.5 rounded-full border border-border bg-card/80 px-3.5 py-1.5 text-sm font-medium text-foreground/80 shadow-sm shadow-broth/5'>
                        <span aria-hidden='true' className='relative flex size-2'>
                            <span className='absolute inline-flex size-full animate-ping rounded-full bg-scallion/60 motion-reduce:animate-none' />
                            <span className='relative inline-flex size-2 rounded-full bg-scallion' />
                        </span>
                        A fresh pot every morning from {firstBowls}
                    </p>

                    <h1
                        id='hero-title'
                        className='mt-7 text-5xl font-semibold leading-[1.02] tracking-tight sm:text-6xl lg:text-7xl'
                    >
                        Chicken porridge,{' '}
                        <em className='font-normal italic text-primary'>
                            simmered overnight.
                        </em>
                    </h1>

                    <p className='mt-6 max-w-xl text-lg leading-relaxed text-muted-foreground'>
                        We simmer whole chickens with ginger until dawn, then stir in jasmine rice until it turns to silk. Every bowl is finished to order with hand-pulled chicken, scallions and crispy shallots.
                    </p>

                    <div className='mt-9 flex flex-col gap-3 sm:flex-row'>
                        <Link href='/#order' className={buttonClasses('primary', 'lg')}>
                            Order ahead for pickup
                            <ArrowRight />
                        </Link>

                        <Link href='/#menu' className={buttonClasses('outline', 'lg')}>
                            See the menu
                        </Link>
                    </div>

                    <ul className='mt-12 grid max-w-xl grid-cols-1 gap-5 border-t border-border pt-7 sm:grid-cols-3'>
                        {highlights.map(item => (
                            <li key={item.title} className='flex items-start gap-3 sm:block'>
                                <span className='inline-flex size-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary'>
                                    <item.icon className='size-5' aria-hidden='true' />
                                </span>

                                <span className='block sm:mt-3'>
                                    <span className='block text-sm font-semibold'>
                                        {item.title}
                                    </span>

                                    <span className='block text-sm text-muted-foreground'>
                                        {item.text}
                                    </span>
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>

                <div className='relative mx-auto w-full max-w-[34rem] animate-in fade-in zoom-in-95 duration-1000 ease-out'>
                    <div className='relative aspect-square overflow-hidden rounded-[2.5rem] bg-primary shadow-2xl shadow-primary/25'>
                        <div aria-hidden='true' className='absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgb(255_255_255/0.18),transparent_55%)]' />
                        <PorridgeIllustration className='relative' />
                    </div>

                    <div className='absolute -left-3 top-8 animate-float rounded-2xl border border-border bg-card px-4 py-3 shadow-xl shadow-broth/10 sm:-left-8 sm:top-12'>
                        <p className='text-[0.7rem] font-semibold uppercase tracking-[0.16em] text-muted-foreground'>
                            Broth on the stove
                        </p>

                        <p className='mt-0.5 font-heading text-lg font-semibold'>
                            Since 10 PM last night
                        </p>
                    </div>

                    <div className='absolute -right-3 bottom-8 animate-float rounded-2xl border border-border bg-card px-4 py-3 shadow-xl shadow-broth/10 [animation-delay:1.5s] sm:-right-8 sm:bottom-12'>
                        <p className='text-[0.7rem] font-semibold uppercase tracking-[0.16em] text-muted-foreground'>
                            Free on the side
                        </p>

                        <p className='mt-0.5 font-heading text-lg font-semibold'>
                            Ginger, scallions, chili oil
                        </p>
                    </div>
                </div>
            </div>
        </section>
    )
}
